import { modalVariants } from '@vgrubs/components';
import { AnimatePresence } from 'framer-motion';
import { ReactNode } from 'react';
import styled from 'styled-components';
import {
  ModalFooter,
  ModalOverlay,
  ModalScrollableStyled,
  ModalStyled,
  ModalWrapper,
} from '../../../components/modalFullscreen/modalFullscreen.styled';
import { GlowBorder, GlowWrapper, Shimmer } from './menuAiGlow.styled';

/**
 * Two-card split shell for the AI feature — menu card (~70%) + AI card (~30%)
 * with a 32px gap. Composed entirely from the exported `modalFullscreen.styled`
 * pieces so the shared `ModalFullScreen`/`Modal` components stay untouched.
 *
 * On mobile the cards stack and the WHOLE group scrolls as one (each card takes
 * its natural height); on desktop each card scrolls independently side by side.
 */
const SplitGroup = styled.div`
  display: flex;
  gap: 32px;
  width: calc(100% - 100px);
  max-width: 1650px;

  @media (max-width: 800px) {
    flex-direction: column;
    gap: 16px;
    width: calc(100% - 32px);
    max-height: calc(100vh - 32px);
    overflow-y: auto;
    /* Vertical scroll only. Without this, overflow-y auto makes the computed
       overflow-x auto too, so the AI working glow (a blurred halo that bleeds a
       few px past the card) triggers an unwanted left-right scroll. */
    overflow-x: hidden;
    ::-webkit-scrollbar {
      display: none;
    }
  }
`;

// The exported ModalStyled caps at `max-width: calc(100% - 100px)` (meant for a
// single centered modal) and defaults to `width: 100%`. In the split row we size
// via flex. On mobile the cards must be `flex: none` (natural content height) —
// a flex ratio in a column would size by HEIGHT and trap the AI card in a tiny box.
const MenuCard = styled(ModalStyled)`
  flex: 1;
  min-width: 0;
  max-width: none;
  width: 100%;
`;

// A flex column so its scroll area can fill the card height — needed so the AI
// "working" view can center vertically within the (tall) card on desktop.
const AiCard = styled(ModalStyled)`
  display: flex;
  flex-direction: column;
  flex: 3 1 0;
  min-width: 0;
  max-width: none;
  width: 100%;

  @media (max-width: 800px) {
    flex: none;
    /* Stacked layout: float the AI controls above the menu preview. */
    order: -1;
  }
`;

// On mobile, stop each card scrolling independently so the SplitGroup is the
// single scroll container — otherwise the nested scroll traps you in the menu
// card and the AI card can't be reached.
// Side padding is tightened from the shared 45px to 32px (mobile stays 24px, in
// step with the footer) so both cards read as a snug 32px gutter.
const InnerScroll = styled(ModalScrollableStyled)`
  padding-left: 32px;
  padding-right: 32px;

  @media (max-width: 800px) {
    padding-left: 24px;
    padding-right: 24px;
    max-height: none;
    overflow: visible;
  }
`;

// The AI card's scroll area. `flex: 1` fills the card height (the card stretches
// to match the taller menu card on desktop). It's a flex column so the active
// tab's content can flex to fill: the tabs row stays pinned at the top
// (flex: none) while the improve panel (flex: 1) fills the rest and vertically
// centers the "working" progress view instead of pinning it under the tabs.
const AiScroll = styled(ModalScrollableStyled)`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding-left: 32px;
  padding-right: 32px;
  /* Footer is two rows (Improve, then Cancel/Save side by side) — reserve enough
     space so the last form field isn't hidden behind it. */
  padding-bottom: 175px;

  @media (max-width: 800px) {
    /* Content height — the footer flows inline right after it (see AiFooter),
       so there's nothing absolutely positioned to reserve space for. In the
       "working" state the progress sits compactly at the top (no tall centered
       gap like on desktop). */
    flex: none;
    padding-left: 24px;
    padding-right: 24px;
    padding-bottom: 24px;
    max-height: none;
    overflow: visible;
  }
`;

// The Cancel/Save footer is an absolute bar on desktop (anchored to the tall AI
// card). On mobile the cards stack into ONE scroll, so an absolute bar would sit
// mid-scroll and clip content behind it — make it flow inline instead.
const AiFooter = styled(ModalFooter)`
  padding-top: 0;
  @media (max-width: 800px) {
    position: static;
    background: none;
  }
`;

export const MenuAISplitModal = ({
  open,
  left,
  right,
  footer,
  animateLeft,
  zIndex,
  onClose,
}: {
  open: boolean;
  left: ReactNode;
  right: ReactNode;
  footer: ReactNode;
  animateLeft?: boolean;
  zIndex?: number;
  onClose?: () => void;
}) => {
  return (
    <AnimatePresence mode="wait">
      {open && (
        // Close on outside click. The wrapper sits ABOVE the overlay, so the
        // click lands here; only close when it's the wrapper itself (empty area
        // around the cards), not a click bubbling up from inside a card.
        <ModalWrapper
          $zIndex={zIndex}
          onClick={(e) => e.target === e.currentTarget && onClose?.()}
        >
          <SplitGroup>
            {/* Menu preview card (~70%) — glows while the AI runs */}
            <GlowWrapper $active={animateLeft}>
              <MenuCard
                key="ai-menu-card"
                variants={modalVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                $zIndex={zIndex}
              >
                {animateLeft && <GlowBorder />}
                {animateLeft && <Shimmer />}
                <InnerScroll>{left}</InnerScroll>
              </MenuCard>
            </GlowWrapper>

            {/* AI controls card (~30%) — owns the Cancel/Save footer */}
            <AiCard
              key="ai-controls-card"
              variants={modalVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              $zIndex={zIndex}
            >
              <AiScroll $footer $numOfFloatingButtons={2}>
                {right}
              </AiScroll>
              {footer && (
                <AiFooter $numOfFloatingButtons={2}>{footer}</AiFooter>
              )}
            </AiCard>
          </SplitGroup>
        </ModalWrapper>
      )}
      {open && (
        <ModalOverlay
          key="ai-overlay"
          variants={modalVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          $zIndex={zIndex}
        />
      )}
    </AnimatePresence>
  );
};
